import { useEffect, useRef, useState } from "react";

import { RealtimeClient, type RealtimeEvent } from "./RealtimeClient";

// Subscribe to realtime events on a scope for the lifetime of the component.
// Pass `scope === null` to skip. The callback is held in a ref so changing its
// identity does not churn the subscription — only the scope string does.
//
// Returns whether access to the scope has been revoked. The server withdraws a
// live subscription the moment a membership changes (realtime.md
// §Authorisation), so this latches: nothing arrives on the scope afterwards,
// and the view should say so rather than sit on data it can no longer refresh.
export const useRealtimeScope = (
  scope: string | null,
  onEvent: (event: RealtimeEvent) => void,
): boolean => {
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;
  const [revoked, setRevoked] = useState(false);

  useEffect(() => {
    setRevoked(false);
    if (!scope) {
      return undefined;
    }
    return RealtimeClient.subscribeScope(scope, (event) => {
      if (event.kind === "scope.revoked") {
        setRevoked(true);
      }
      callbackRef.current(event);
    });
  }, [scope]);

  return revoked;
};
